import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';

const fetchWithAuth = async (url, options = {}) => {
  const token = getToken();
  const res = await fetch(url, {
    ...options,
    headers: { ...options.headers, 'x-token': token }
  });
  if (res.status === 401) {
    localStorage.removeItem('notes_token');
    localStorage.removeItem('notes_user');
    window.location.href = '/';
  }
  return res;
};

export default function ProjectDocOverview() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 筛选条件
  const [filterProject, setFilterProject] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterUploadedBy, setFilterUploadedBy] = useState('');
  const [filterKeyword, setFilterKeyword] = useState('');
  const [selectedDocs, setSelectedDocs] = useState([]);

  useEffect(() => {
    loadProjects();
    loadDocuments();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/doc-projects`);
      const data = await res.json();
      setProjects(data);
    } catch (e) {
      console.error('加载项目列表失败:', e);
    }
  };

  const loadDocuments = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.projectId) params.append('projectId', filters.projectId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.uploadedBy) params.append('uploadedBy', filters.uploadedBy);
      if (filters.keyword) params.append('keyword', filters.keyword);
      
      const url = `${API_URL}/api/doc-overview${params.toString() ? '?' + params.toString() : ''}`;
      const res = await fetchWithAuth(url);
      const data = await res.json();
      setDocuments(data);
    } catch (e) {
      console.error('加载文档列表失败:', e);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    loadDocuments({
      projectId: filterProject,
      startDate: filterStartDate,
      endDate: filterEndDate,
      uploadedBy: filterUploadedBy,
      keyword: filterKeyword
    });
  };

  const handleReset = () => {
    setFilterProject('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterUploadedBy('');
    setFilterKeyword('');
    setSelectedDocs([]);
    loadDocuments();
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDocs(documents.map(d => d.id));
    } else {
      setSelectedDocs([]);
    }
  };

  const handleSelectDoc = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleBatchDownload = async () => {
    if (selectedDocs.length === 0) {
      alert('请先选择要下载的文档');
      return;
    }

    const selectedDocsList = documents.filter(d => selectedDocs.includes(d.id));
    
    // 逐个下载
    for (const doc of selectedDocsList) {
      try {
        const res = await fetchWithAuth(`${API_URL}/api/doc-files/${doc.id}/download`);
        const data = await res.json();
        if (data.success && data.url) {
          // 创建隐藏的a标签触发下载
          const link = document.createElement('a');
          link.href = data.url;
          link.download = doc.fileName;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          // 延迟一下避免浏览器阻止多个下载
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (e) {
        console.error(`下载 ${doc.fileName} 失败:`, e);
      }
    }
    
    alert(`已开始下载 ${selectedDocs.length} 个文件`);
    setSelectedDocs([]);
  };

  const handleDownload = async (fileId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/doc-files/${fileId}/download`);
      const data = await res.json();
      if (data.success && data.url) {
        window.open(data.url, '_blank');
      }
    } catch (e) {
      alert('获取下载链接失败');
    }
  };

  const handlePreview = async (fileId, fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
    const pdfExts = ['pdf'];
    const officeExts = ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'];
    
    try {
      const res = await fetchWithAuth(`${API_URL}/api/doc-files/${fileId}/preview`);
      const data = await res.json();
      if (data.success && data.url) {
        // Office文档使用微软在线预览
        if (officeExts.includes(ext)) {
          const previewUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(data.url)}`;
          window.open(previewUrl, '_blank');
        } else if (imageExts.includes(ext) || pdfExts.includes(ext)) {
          // 图片和PDF直接打开
          window.open(data.url, '_blank');
        } else {
          alert('该文件类型暂不支持在线预览，请下载后查看');
        }
      }
    } catch (e) {
      alert('获取预览链接失败');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(1)} ${units[i]}`;
  };

  // 获取所有上传人
  const uploaders = [...new Set(documents.map(d => d.uploadedBy))];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button 
            onClick={() => navigate('/work-logs/doc-projects')}
            style={{
              padding: '6px 12px', background: '#fff', border: '1px solid #d9d9d9',
              borderRadius: 6, cursor: 'pointer', fontSize: 14
            }}
          >
            ← 返回
          </button>
          <h2 style={{ margin: 0 }}>📋 文档总览</h2>
        </div>
      </div>

      {/* 筛选区域 */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8',
        padding: 20, marginTop: 24, marginBottom: 20
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#666' }}>项目</label>
            <select
              value={filterProject}
              onChange={e => setFilterProject(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9',
                borderRadius: 6, fontSize: 13, boxSizing: 'border-box'
              }}
            >
              <option value="">全部项目</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#666' }}>开始日期</label>
            <input
              type="date"
              value={filterStartDate}
              onChange={e => setFilterStartDate(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9',
                borderRadius: 6, fontSize: 13, boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#666' }}>结束日期</label>
            <input
              type="date"
              value={filterEndDate}
              onChange={e => setFilterEndDate(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9',
                borderRadius: 6, fontSize: 13, boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#666' }}>上传人</label>
            <select
              value={filterUploadedBy}
              onChange={e => setFilterUploadedBy(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9',
                borderRadius: 6, fontSize: 13, boxSizing: 'border-box'
              }}
            >
              <option value="">全部上传人</option>
              {uploaders.map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#666' }}>文件名</label>
            <input
              type="text"
              value={filterKeyword}
              onChange={e => setFilterKeyword(e.target.value)}
              placeholder="模糊搜索文件名"
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9',
                borderRadius: 6, fontSize: 13, boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: 12, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            onClick={handleReset}
            style={{
              padding: '8px 16px', background: '#fff', border: '1px solid #d9d9d9',
              borderRadius: 6, cursor: 'pointer', fontSize: 13
            }}
          >
            重置
          </button>
          <button
            onClick={handleSearch}
            style={{
              padding: '8px 16px', background: '#1890ff', color: '#fff',
              border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13
            }}
          >
            🔍 搜索
          </button>
        </div>
      </div>

      {/* 文档列表 */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: 20 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>加载中...</div>
        ) : documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
            <p>暂无文档</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 13, color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>共 {documents.length} 个文档</span>
              {selectedDocs.length > 0 && (
                <button
                  onClick={handleBatchDownload}
                  style={{
                    padding: '6px 14px', background: '#1890ff', color: '#fff',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13
                  }}
                >
                  📥 批量下载 ({selectedDocs.length})
                </button>
              )}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #e8e8e8' }}>
                  <th style={{ padding: '12px 8px', textAlign: 'center', width: 40 }}>
                    <input
                      type="checkbox"
                      checked={documents.length > 0 && selectedDocs.length === documents.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>文件名</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>项目</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>文件夹</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>大小</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>上传人</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>上传时间</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, fontWeight: 600 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id} style={{ borderBottom: '1px solid #f0f0f0', background: selectedDocs.includes(doc.id) ? '#e6f7ff' : 'transparent' }}>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedDocs.includes(doc.id)}
                        onChange={() => handleSelectDoc(doc.id)}
                      />
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                      📄 {doc.fileName}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#666' }}>
                      {doc.projectName}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#666' }}>
                      {doc.folderName}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#666' }}>
                      {formatSize(doc.size)}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#666' }}>
                      {doc.uploadedBy}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#999' }}>
                      {new Date(doc.uploadedAt).toLocaleString('zh-CN')}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button
                          onClick={() => handlePreview(doc.id, doc.fileName)}
                          style={{
                            padding: '4px 10px', background: '#e6f7ff', border: '1px solid #91d5ff',
                            borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#1890ff'
                          }}
                        >
                          预览
                        </button>
                        <button
                          onClick={() => handleDownload(doc.id)}
                          style={{
                            padding: '4px 10px', background: '#f6ffed', border: '1px solid #b7eb8f',
                            borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#52c41a'
                          }}
                        >
                          下载
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
